import type { ReactNode } from 'react';
import { ProgressBar } from 'react-aria-components';
import { cn } from '../lib/cn';

export interface ProgressRingProps {
  value: number;
  max: number;
  /** Accessible label, e.g. "Fortschritt zu Familienkino". */
  label: string;
  /** Spoken value, e.g. "75 von 200 Sternen". Defaults to a percentage. */
  valueLabel?: string;
  size?: 'md' | 'lg';
  tone?: 'points' | 'success' | 'action';
  children?: ReactNode;
  className?: string;
}

const tones = {
  points: 'stroke-reward-points',
  success: 'stroke-feedback-success',
  action: 'stroke-action-primary',
};

const R = 42;
const C = 2 * Math.PI * R;

export function ProgressRing({
  value,
  max,
  label,
  valueLabel,
  size = 'md',
  tone = 'points',
  children,
  className,
}: ProgressRingProps) {
  const ratio = max > 0 ? Math.min(Math.max(value / max, 0), 1) : 0;
  return (
    <ProgressBar
      value={value}
      maxValue={max}
      aria-label={label}
      valueLabel={valueLabel}
      className={cn(
        'relative inline-grid place-items-center',
        size === 'md' ? 'size-avatar-lg' : 'size-avatar-xl',
        className,
      )}
    >
      <svg viewBox="0 0 100 100" className="absolute inset-0 size-full -rotate-90" aria-hidden>
        <circle
          cx="50"
          cy="50"
          r={R}
          fill="none"
          strokeWidth="10"
          className="stroke-border-subtle"
        />
        <circle
          cx="50"
          cy="50"
          r={R}
          fill="none"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={C * (1 - ratio)}
          className={cn(
            tones[tone],
            'transition-all duration-(--shh-motion-duration-slow) ease-standard',
          )}
        />
      </svg>
      <span className="relative text-center font-display font-bold leading-tight">{children}</span>
    </ProgressBar>
  );
}
